import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Checkbox, DatePicker, Form, Input, Modal, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import dayjs, { Dayjs } from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { OwnerItem, SupportLine } from '@totem/components/supportLines/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
const { RangePicker } = DatePicker;
interface Props {
  visible: boolean;
  onClose: (refresh: boolean) => void;
  owner: OwnerItem;
  supportLine?: SupportLine;
}
const SupportLineEditDialog = ({
  visible,
  onClose,
  owner,
  supportLine,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const currentTime = dayjs();
  const [isSending, setIsSending] = useState(false);
  const [line, setLine] = useState<SupportLine>(
    isNotNull(supportLine)
      ? supportLine
      : {
          id: EMPTY_ID,
          startDate: currentTime.toISOString(),
          endDate: currentTime.toISOString(),
          autoRenewable: false,
          companyName: '',
          contractContact: {
            name: '',
            phone: '',
            email: '',
          },
          supportContact: {
            name: '',
            phone: '',
            email: '',
          },
          serviceLevelAgreement: '',
          createdBy: userProfile.email,
          createdAt: currentTime.toISOString(),
          updatedBy: userProfile.email,
          updatedAt: currentTime.toISOString(),
        },
  );

  useEffect(() => {
    if (isNotNull(supportLine)) {
      setLine(supportLine);
    }
  }, [supportLine]);

  const sendAddUpdateSupportLine = useCallback(
    async (paramOwner: OwnerItem, paramLine: SupportLine) => {
      const payload = {
        ownerItem: paramOwner,
        supportLine: paramLine,
      };
      fetch(`${DEVICES_ENDPOINT}/supportLines`, {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        setIsSending(false);
        const success = CheckResponseShowError(res);
        if (success) {
          onClose(true);
        }
      });
    },
    [],
  );

  const isValid = () => {
    let isFormValid = true;
    if (line.startDate > line.endDate) {
      isFormValid = false;

      notification.error({
        title: 'Invalid Date/Time Entry!',
        description: 'End time must be after start time!',
        duration: 3,
      });
    }
    if (line.companyName === '') {
      isFormValid = false;

      notification.error({
        title: 'Invalid Company Name!',
        description: 'Company Name Required!',
        duration: 3,
      });
    }
    return isFormValid;
  };

  const handleSubmit = () => {
    if (isValid()) {
      sendAddUpdateSupportLine(owner, line);
    }
  };
  return (
    <Modal
      title={supportLine ? 'Update Support Line' : 'Create Support Line'}
      open={visible}
      onCancel={() => onClose(false)}
      onOk={isSending ? null : handleSubmit}
      okText={supportLine ? 'Update' : 'Create'}
    >
      <Form layout="vertical">
        <Form.Item label="Company Name" required>
          <Input
            name="companyName"
            type="text"
            value={line.companyName}
            onChange={(evt) =>
              setLine((prevState) => ({
                ...prevState,
                companyName: evt.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label="Contract Duration" required>
          <RangePicker
            value={[dayjs(line.startDate), dayjs(line.endDate)]}
            onChange={(dates: null | (Dayjs | null)[]) =>
              setLine((prevState) => ({
                ...prevState,
                startDate: dates[0].toISOString(),
                endDate: dates[1].toISOString(),
              }))
            }
          />
        </Form.Item>
        <Form.Item>
          <Checkbox
            checked={line.autoRenewable}
            onChange={(evt: CheckboxChangeEvent) =>
              setLine((prevState) => ({
                ...prevState,
                autoRenewable: evt.target.checked,
              }))
            }
          >
            Auto Renewable
          </Checkbox>
        </Form.Item>
        <Form.Item label="Service Level Agreement">
          <Input
            value={line.serviceLevelAgreement}
            onChange={(evt) =>
              setLine((prevState) => ({
                ...prevState,
                serviceLevelAgreement: evt.target.value,
              }))
            }
          />
        </Form.Item>
        <IBTabs
          items={[
            {
              key: 'contract',
              label: 'Contract Contact',
              children: (
                <>
                  <Form.Item label="Name">
                    <Input
                      value={line.contractContact.name}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          contractContact: {
                            ...prevState.contractContact,
                            name: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Phone">
                    <Input
                      value={line.contractContact.phone}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          contractContact: {
                            ...prevState.contractContact,
                            phone: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Email">
                    <Input
                      value={line.contractContact.email}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          contractContact: {
                            ...prevState.contractContact,
                            email: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'support',
              label: 'Support Contact',
              children: (
                <>
                  <Form.Item label="Name">
                    <Input
                      value={line.supportContact.name}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          supportContact: {
                            ...prevState.supportContact,
                            name: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Phone">
                    <Input
                      value={line.supportContact.phone}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          supportContact: {
                            ...prevState.supportContact,
                            phone: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Email">
                    <Input
                      value={line.supportContact.email}
                      onChange={(evt) =>
                        setLine((prevState) => ({
                          ...prevState,
                          supportContact: {
                            ...prevState.supportContact,
                            email: evt.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};
export default SupportLineEditDialog;
